/**
 * Time range options for analytics filtering.
 * Values: 'day' | 'week' | 'month' | 'quarter' | 'year'
 */
export const TIME_RANGE_OPTIONS = [
  { value: 'all', label: 'All time' },
  { value: 'day', label: 'Today' },
  { value: 'week', label: 'Last 7 Days' },
  { value: 'month', label: 'Last 30 Days' },
  { value: 'quarter', label: 'Last 3 Months' },
  { value: 'year', label: 'Last 12 Months' },
];

/**
 * Returns { start, end } as Date objects for the given time range.
 * End is "now" (inclusive); start is the beginning of the period.
 */
export function getDateRangeForTimePeriod(timeRange = 'month') {
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  let start;

  switch (timeRange) {
    case 'day':
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
      break;
    case 'week':
      start = new Date(now);
      start.setDate(start.getDate() - 6);
      start.setHours(0, 0, 0, 0);
      break;
    case 'month':
      start = new Date(now);
      start.setDate(start.getDate() - 29);
      start.setHours(0, 0, 0, 0);
      break;
    case 'quarter':
      start = new Date(now);
      start.setDate(start.getDate() - 89);
      start.setHours(0, 0, 0, 0);
      break;
    case 'year':
      start = new Date(now);
      start.setDate(start.getDate() - 364);
      start.setHours(0, 0, 0, 0);
      break;
    default:
      start = new Date(now);
      start.setDate(start.getDate() - 29);
      start.setHours(0, 0, 0, 0);
  }

  return { start, end };
}

/**
 * Check if a date string or Date falls within the time range.
 */
export function isInTimeRange(dateValue, timeRange = 'month') {
  if (!dateValue) return false;
  const { start, end } = getDateRangeForTimePeriod(timeRange);
  const d = new Date(dateValue);
  return d >= start && d <= end;
}

/**
 * Get chart bucket config for a time range (label, key, count).
 * Returns array of { key, label } for grouping data.
 */
export function getBucketsForTimeRange(timeRange = 'month') {
  const now = new Date();
  const buckets = [];

  switch (timeRange) {
    case 'day':
      buckets.push({
        key: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`,
        label: 'Today',
      });
      break;
    case 'week': {
      for (let i = 6; i >= 0; i--) {
        const d = new Date(now);
        d.setDate(d.getDate() - i);
        const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        buckets.push({ key: k, label: d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }) });
      }
      break;
    }
    case 'month': {
      for (let i = 29; i >= 0; i--) {
        const d = new Date(now);
        d.setDate(d.getDate() - i);
        const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        buckets.push({ key: k, label: d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) });
      }
      break;
    }
    case 'quarter': {
      for (let i = 2; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        buckets.push({ key: k, label: d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' }) });
      }
      break;
    }
    case 'year':
    default: {
      for (let i = 11; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        buckets.push({ key: k, label: d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' }) });
      }
      break;
    }
  }
  return buckets;
}

/**
 * Get date key for an order/item to match bucket (orderDate, $createdAt, etc).
 */
export function getDateKeyForBucket(dateValue, timeRange) {
  if (!dateValue) return null;
  const d = new Date(dateValue);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  if (timeRange === 'day' || timeRange === 'week' || timeRange === 'month') {
    return `${y}-${m}-${day}`;
  }
  return `${y}-${m}`;
}

/**
 * Filter an array of items by date. Expects each item to have orderDate, $createdAt, or similar.
 * Tries common date field names.
 */
export function filterByTimeRange(items, timeRange = 'month', dateField = null) {
  if (!items || items.length === 0) return items;
  if (!timeRange) return items;
  const { start, end } = getDateRangeForTimePeriod(timeRange);
  const fields = dateField ? [dateField] : ['orderDate', 'order_date', '$createdAt', 'createdAt', 'date'];
  return items.filter((item) => {
    const val = fields.map((f) => item[f]).find((v) => v != null);
    if (!val) return false;
    const d = new Date(val);
    return d >= start && d <= end;
  });
}
